import { useRef } from "react";
import {
  useGetRequestedLogisticsQuotesQuery,
  useUpdateLogisticsQuoteMutation,
} from "../../../store/apiSlices/logistics";
import { LogisticsRequestQuote } from "../../../types/PartnerCompanyDetails";
import { getRFQStatusStyles } from "../../Admin/CustomerRFQsDetails/RFQCard";
import { AirPlaneIcon, MapPinIcon, ShipIcon, SpinnerIcon } from "../../Icons";
import Modal from "../../Modal";
import { RFQSkeletonList } from "./RFQs";
import QuoteCard from "./QuoteCard";
import { calculatePriceRange } from "../../../utils";

export default function Quotes({
  onSelect,
}: {
  onSelect: (item: any) => void;
}) {
  const { data, isLoading, isError, refetch, isFetching } =
    useGetRequestedLogisticsQuotesQuery();

  if (isLoading) return <RFQSkeletonList />;

  if (isError)
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8">
        <p className="text-sm text-gray-500">Failed to load quotes.</p>

        <button
          onClick={refetch}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700
             disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isFetching && (
            <SpinnerIcon className="size-3 text-gray-200 animate-spin fill-gray-600" />
          )}
          Try again
        </button>
      </div>
    );

  const quotes = data?.filter((el) => el.status === "active") || [];

  if (!quotes.length)
    return <p className="text-center text-sm text-gray-400">No quotes found</p>;

  return (
    <div className="space-y-3">
      {quotes.map((quote) => (
        <QuoteCard
          key={quote.id || quote.quotes_id}
          quote={quote}
          onSelect={onSelect}
        >
          <QuoteCard.Header>
            <QuoteCard.FromTo />
            <QuoteCard.Status />
          </QuoteCard.Header>
          <QuoteCard.Body>
            <QuoteCard.FreightInfo />
            <QuoteCard.PickupDate />
          </QuoteCard.Body>
          <QuoteCard.Footer>
            <QuoteCard.Price />
            <QuoteCard.CreatedAt />
          </QuoteCard.Footer>
        </QuoteCard>
      ))}
    </div>
  );
}

/* Expanded view for a single Draft item */
Quotes.Expanded = function QuotesExpanded({
  selectedQuoteId,
  onClose,
}: {
  selectedQuoteId: LogisticsRequestQuote["id"];
  onClose: () => void;
}) {
  const modalRef = useRef<HTMLDialogElement>(null);
  const { data } = useGetRequestedLogisticsQuotesQuery();

  const selectedQuote = data?.find((q) => q.id === selectedQuoteId);

  if (!selectedQuote) return null;

  const {
    from_json,
    to_json,
    ai_response,
    freight_type,
    // loading_date,
    pickup_date,
    goods_category,
    weight,
    status,
    incoterm, // 🆕 Added
    actual_quotes,
    id,
  } = selectedQuote;

  const quote = ai_response?.quote || {};
  // const breakdown = quote.breakdown || {};

  const FreightIcon =
    freight_type?.toLowerCase() === "air" ? AirPlaneIcon : ShipIcon;

  const { label, className: statusColor } = getRFQStatusStyles(status);

  const [updateLogisticsQuote, { isLoading }] =
    useUpdateLogisticsQuoteMutation();

  const toggleModal = () => {
    if (modalRef.current?.open) {
      modalRef.current.close();
      // close the quotes tab
      onClose();
    } else {
      modalRef.current?.showModal();
    }
  };

  const handleAcceptQuote = async (
    actual_quote_id: LogisticsRequestQuote["actual_quotes"][number]["actual_quote_id"],
  ) => {
    const payload = {
      id,
      actual_quote_id,
      quote_status:
        "confirmed" as LogisticsRequestQuote["actual_quotes"][number]["status"],
    };

    const { data } = await updateLogisticsQuote(payload);

    if (data.status === "success") {
      // show the success modal
      toggleModal();
    }
  };

  const handleDenyQuote = async (
    actual_quote_id: LogisticsRequestQuote["actual_quotes"][number]["actual_quote_id"],
  ) => {
    const payload = {
      id,
      actual_quote_id,
      quote_status:
        "denied" as LogisticsRequestQuote["actual_quotes"][number]["status"],
    };

    await updateLogisticsQuote(payload);
  };

  return (
    <div className="h-full overflow-y-auto p-5 bg-white border border-gray-100 rounded-xl shadow-sm">
      {/* Back Button */}
      <button
        onClick={onClose}
        className="mb-4 text-sm text-blue-600 hover:underline"
      >
        ← Back to Quotes
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MapPinIcon className="size-4 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-800">
              {from_json?.name} → {to_json?.name}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FreightIcon className="size-4 text-blue-500" />
            <span>{freight_type?.toUpperCase()} Freight</span>
            <span className="text-gray-400">|</span>
            <span>{weight || "—"} kg</span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span>
              <span className="font-medium text-gray-600">Incoterm:</span>{" "}
              <span className="italic">
                {incoterm ? incoterm.toUpperCase() : "—"}
              </span>
            </span>

            <span className="text-gray-300">|</span>

            <span>
              <span className="font-medium text-gray-600">Goods:</span>{" "}
              <span className="text-gray-600">
                {goods_category || "Not specified"}
              </span>
            </span>
          </div>

          <p className="text-xs text-gray-400 mt-1">
            {/* Loading: {loading_date || "—"} |  */}
            Pickup: {pickup_date || "—"}
          </p>
        </div>
        <span
          className={`text-xs px-2 py-1 rounded-full font-medium h-fit ${statusColor}`}
        >
          {label || "Pending"}
        </span>
      </div>

      {/* Quote Overview */}
      {quote?.estimated_price_usd ? (
        <div className="relative border border-green-100 bg-green-50 rounded-lg p-4 mb-5">
          <div className="absolute -top-2 -right-2 bg-green-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm">
            Estimate
          </div>
          <p className="text-base font-semibold text-green-700">
            {calculatePriceRange(quote.estimated_price_usd).formatted("$")}
          </p>
          <p className="text-xs text-green-600 mt-1">
            Valid for {quote.validity_days || "—"} days • Transit:{" "}
            {quote.estimated_transit_days || "—"} days
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500 mb-5">
          No quote available yet. Awaiting partner responses.
        </p>
      )}

      {actual_quotes?.length > 0 && (
        <div className="relative border-t border-gray-100 pt-4">
          {/* Backdrop Overlay */}
          {isLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-lg">
              <div className="flex flex-col items-center gap-2">
                <SpinnerIcon className="size-6 animate-spin fill-custom-dark-blue" />
                <p className="text-xs text-gray-600 font-medium">
                  Processing quote...
                </p>
              </div>
            </div>
          )}

          <div className={isLoading ? "opacity-60 pointer-events-none" : ""}>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Available Quotes
            </h3>

            <div className="space-y-3">
              {actual_quotes.map((quote) => (
                <div
                  key={quote.actual_quote_id}
                  className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                >
                  {/* Vendor */}
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {quote.vendor}
                    </p>
                    <p className="text-xs text-gray-500">
                      Expires: {quote.expiry || "—"}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Price */}
                    <p className="text-sm font-semibold text-gray-800">
                      ${quote.price.toLocaleString()}
                    </p>

                    {/* Buttons */}
                    <div className="flex gap-2">
                      {quote.status === "denied" ? (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-50 text-red-600 border border-red-200">
                          Declined
                        </span>
                      ) : (
                        <>
                          <button
                            onClick={() =>
                              handleAcceptQuote(quote.actual_quote_id)
                            }
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition"
                          >
                            Accept
                          </button>

                          <button
                            onClick={() =>
                              handleDenyQuote(quote.actual_quote_id)
                            }
                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-500 text-red-600 hover:bg-red-50 transition"
                          >
                            Deny
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Modal
        ref={modalRef}
        toggleDialog={toggleModal}
        className="max-w-md w-full rounded-2xl bg-white p-6 shadow-2xl backdrop:bg-black/50"
      >
        {/* Success Icon */}
        <div className="flex justify-center mb-5">
          <div className="bg-green-100 text-green-600 rounded-full p-4 shadow-sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 text-center">
          Quote Accepted Successfully 🎉
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-sm mt-3 text-center leading-relaxed">
          You’ve successfully accepted this vendor’s quote. The vendor has been
          notified and the shipment process will begin shortly.
        </p>

        {/* Divider */}
        <div className="w-full h-[1px] bg-gray-200 my-6"></div>

        {/* Actions */}
        <div className="flex justify-center">
          <button
            onClick={toggleModal}
            className="px-6 py-2.5 text-sm font-medium rounded-xl bg-custom-dark-blue text-white hover:opacity-90 transition shadow-sm"
          >
            Continue
          </button>
        </div>
      </Modal>
    </div>
  );
};
